import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AuthProvider } from './context/AuthContext';
import { SiteConfigProvider } from './context/SiteConfigContext';
import { ATSAuthProvider } from './context/ATSAuthContext';
import { ATSDataProvider } from './context/ATSDataContext';
import Navigation from './components/public/Navigation';
import Footer from './components/public/Footer';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from './components/admin/ProtectedRoute';
import ATSProtectedRoute from './components/ats/ATSProtectedRoute';
import { Toaster } from '@/components/ui/sonner';
import './App.css';

// Lazy load pages for better performance
const Home = lazy(() => import('./pages/Home'));
const JobsPage = lazy(() => import('./pages/JobsPage'));
const JobDetailPage = lazy(() => import('./pages/JobDetailPage'));
const ApplyPage = lazy(() => import('./pages/ApplyPage'));
const ReferPage = lazy(() => import('./pages/ReferPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const CulturePage = lazy(() => import('./pages/CulturePage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const TrackPage = lazy(() => import('./pages/TrackPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminJobs = lazy(() => import('./pages/admin/AdminJobs'));
const AdminCandidates = lazy(() => import('./pages/admin/AdminCandidates'));
const AdminReferrals = lazy(() => import('./pages/admin/AdminReferrals'));
const AdminEnquiries = lazy(() => import('./pages/admin/AdminEnquiries'));
const AdminKPI = lazy(() => import('./pages/admin/AdminKPI'));
const AdminLeadership = lazy(() => import('./pages/admin/AdminLeadership'));
const AdminSiteSettings = lazy(() => import('./pages/admin/AdminSiteSettings'));
const AdminPartners = lazy(() => import('./pages/admin/AdminPartners'));

// ATS Pages
const ATSLogin = lazy(() => import('./pages/ats/ATSLogin'));
const ATSDashboard = lazy(() => import('./pages/ats/ATSDashboard'));
const ATSEmployees = lazy(() => import('./pages/ats/ATSEmployees'));
const ATSClients = lazy(() => import('./pages/ats/ATSClients'));
const ATSVendors = lazy(() => import('./pages/ats/ATSVendors'));
const ATSRequirements = lazy(() => import('./pages/ats/ATSRequirements'));
const ATSProfiles = lazy(() => import('./pages/ats/ATSProfiles'));
const ATSBusiness = lazy(() => import('./pages/ats/ATSBusiness'));
const ATSUsers = lazy(() => import('./pages/ats/ATSUsers'));
const ATSSettings = lazy(() => import('./pages/ats/ATSSettings'));

// Page transition variants
const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.2, ease: 'easeIn' } }
};

// Loading fallback with skeleton - Updated for light theme
const PageLoader = () => (
  <div className="min-h-screen bg-white flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="relative">
        <div className="w-12 h-12 border-3 border-primary/20 rounded-full" />
        <div className="absolute top-0 left-0 w-12 h-12 border-3 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
      <span className="text-primary/40 text-sm font-medium">Loading...</span>
    </div>
  </div>
);

// Animated page wrapper
const AnimatedPage = ({ children }) => (
  <motion.div
    variants={pageVariants}
    initial="initial"
    animate="animate"
    exit="exit"
  >
    {children}
  </motion.div>
);

// Scroll restoration on route change
const ScrollRestoration = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  
  return null;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isATSRoute = location.pathname.startsWith('/ats');

  return (
    <>
      <ScrollRestoration />
      {!isAdminRoute && !isATSRoute && <Navigation />}
      <Suspense fallback={<PageLoader />}>
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            {/* Public Routes */}
            <Route path="/" element={<AnimatedPage><Home /></AnimatedPage>} />
            <Route path="/jobs" element={<AnimatedPage><JobsPage /></AnimatedPage>} />
            <Route path="/jobs/:jobId" element={<AnimatedPage><JobDetailPage /></AnimatedPage>} />
            <Route path="/apply" element={<AnimatedPage><ApplyPage /></AnimatedPage>} />
            <Route path="/refer" element={<AnimatedPage><ReferPage /></AnimatedPage>} />
            <Route path="/about" element={<AnimatedPage><AboutPage /></AnimatedPage>} />
            <Route path="/services" element={<AnimatedPage><ServicesPage /></AnimatedPage>} />
            <Route path="/culture" element={<AnimatedPage><CulturePage /></AnimatedPage>} />
            <Route path="/contact" element={<AnimatedPage><ContactPage /></AnimatedPage>} />
            <Route path="/track" element={<AnimatedPage><TrackPage /></AnimatedPage>} />
            <Route path="/privacy" element={<AnimatedPage><PrivacyPage /></AnimatedPage>} />
            <Route path="/terms" element={<AnimatedPage><TermsPage /></AnimatedPage>} />
            
            {/* Admin Routes */}
            <Route path="/admin" element={<AnimatedPage><AdminLogin /></AnimatedPage>} />
            <Route path="/admin/dashboard" element={<ProtectedRoute><AnimatedPage><AdminDashboard /></AnimatedPage></ProtectedRoute>} />
            <Route path="/admin/jobs" element={<ProtectedRoute><AnimatedPage><AdminJobs /></AnimatedPage></ProtectedRoute>} />
            <Route path="/admin/candidates" element={<ProtectedRoute><AnimatedPage><AdminCandidates /></AnimatedPage></ProtectedRoute>} />
            <Route path="/admin/referrals" element={<ProtectedRoute><AnimatedPage><AdminReferrals /></AnimatedPage></ProtectedRoute>} />
            <Route path="/admin/enquiries" element={<ProtectedRoute><AnimatedPage><AdminEnquiries /></AnimatedPage></ProtectedRoute>} />
            <Route path="/admin/kpi" element={<ProtectedRoute><AnimatedPage><AdminKPI /></AnimatedPage></ProtectedRoute>} />
            <Route path="/admin/leadership" element={<ProtectedRoute><AnimatedPage><AdminLeadership /></AnimatedPage></ProtectedRoute>} />
            <Route path="/admin/partners" element={<ProtectedRoute><AnimatedPage><AdminPartners /></AnimatedPage></ProtectedRoute>} />
            <Route path="/admin/site-settings" element={<ProtectedRoute><AnimatedPage><AdminSiteSettings /></AnimatedPage></ProtectedRoute>} />
            
            {/* ATS Routes */}
            <Route path="/ats" element={<AnimatedPage><ATSLogin /></AnimatedPage>} />
            <Route path="/ats/login" element={<AnimatedPage><ATSLogin /></AnimatedPage>} />
            <Route path="/ats/dashboard" element={<ATSProtectedRoute><AnimatedPage><ATSDashboard /></AnimatedPage></ATSProtectedRoute>} />
            <Route path="/ats/employees" element={<ATSProtectedRoute><AnimatedPage><ATSEmployees /></AnimatedPage></ATSProtectedRoute>} />
            <Route path="/ats/clients" element={<ATSProtectedRoute><AnimatedPage><ATSClients /></AnimatedPage></ATSProtectedRoute>} />
            <Route path="/ats/vendors" element={<ATSProtectedRoute><AnimatedPage><ATSVendors /></AnimatedPage></ATSProtectedRoute>} />
            <Route path="/ats/requirements" element={<ATSProtectedRoute><AnimatedPage><ATSRequirements /></AnimatedPage></ATSProtectedRoute>} />
            <Route path="/ats/profiles" element={<ATSProtectedRoute><AnimatedPage><ATSProfiles /></AnimatedPage></ATSProtectedRoute>} />
            <Route path="/ats/business" element={<ATSProtectedRoute><AnimatedPage><ATSBusiness /></AnimatedPage></ATSProtectedRoute>} />
            <Route path="/ats/users" element={<ATSProtectedRoute><AnimatedPage><ATSUsers /></AnimatedPage></ATSProtectedRoute>} />
            <Route path="/ats/settings" element={<ATSProtectedRoute><AnimatedPage><ATSSettings /></AnimatedPage></ATSProtectedRoute>} />
            
            {/* 404 - Must be last */}
            <Route path="*" element={<AnimatedPage><NotFoundPage /></AnimatedPage>} />
          </Routes>
        </AnimatePresence>
      </Suspense>
      {!isAdminRoute && !isATSRoute && <Footer />}
      {!isAdminRoute && !isATSRoute && <ScrollToTop />}
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <SiteConfigProvider>
        <ATSAuthProvider>
          <ATSDataProvider>
            <BrowserRouter>
              <div className="App">
                <AnimatedRoutes />
                <Toaster 
                  position="top-right"
                  toastOptions={{
                    style: {
                      background: '#1E3A5F',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: '#fff',
                    },
                  }}
                />
              </div>
            </BrowserRouter>
          </ATSDataProvider>
        </ATSAuthProvider>
      </SiteConfigProvider>
    </AuthProvider>
  );
}

export default App;
